import React from 'react';
import { LucideIcon } from 'lucide-react';

export interface StatsCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  testId?: string;
  loading?: boolean;
}

// Skeleton loader component
const StatsCardSkeleton: React.FC<{ testId?: string }> = ({ testId }) => (
  <div
    className="bg-white rounded-xl shadow-card border border-neutral-100 p-6 animate-pulse"
    data-testid={`${testId}-skeleton`}
  >
    <div className="flex items-center justify-between">
      <div className="space-y-3">
        <div className="h-4 w-24 bg-neutral-200 rounded" />
        <div className="h-9 w-20 bg-neutral-200 rounded" />
      </div>
      <div className="h-12 w-12 rounded-full bg-neutral-200" />
    </div>
    <div className="mt-4">
      <div className="h-4 w-32 bg-neutral-200 rounded" />
    </div>
  </div>
);

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor = 'text-primary-600',
  iconBgColor = 'bg-primary-100',
  testId,
  loading = false,
}) => {
  if (loading) {
    return <StatsCardSkeleton testId={testId} />;
  }

  return (
    <div
      className="bg-white rounded-xl shadow-card border border-neutral-100 p-6
                 transition-all duration-200 ease-in-out
                 hover:shadow-card-hover hover:border-neutral-200
                 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2"
      data-testid={testId}
      tabIndex={0}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-500 truncate">{title}</p>
          <p
            className="text-3xl font-bold text-neutral-900 mt-1 tracking-tight"
            data-testid={`${testId}-value`}
          >
            {value}
          </p>
        </div>
        <div
          className={`h-12 w-12 rounded-xl ${iconBgColor} flex items-center justify-center
                     flex-shrink-0 ml-4 transition-transform duration-200 hover:scale-105`}
        >
          <Icon className={`h-6 w-6 ${iconColor}`} />
        </div>
      </div>
      {subtitle && (
        <div
          className="mt-4 flex items-center text-sm text-neutral-500 pt-4 border-t border-neutral-100"
          data-testid={`${testId}-subtitle`}
        >
          <span className="truncate">{subtitle}</span>
        </div>
      )}
    </div>
  );
};

export default StatsCard;
